import {
  ERROR_SESSION_EXPIRED,
  ERROR_TIMEOUT,
  ERROR_UNKNOWN,
} from "constants/messages";
import { getAuth, onIdTokenChanged, signOut } from "firebase/auth";
import { isTokenExpired } from "utils/auth";
import { filterObjToGetUrl } from "utils/stringbuilders";

// "Download all" fetches every page of a view sequentially, so the real cost is
// round-trips, not payload size. Larger pages mean fewer trips; 500 is a
// conservative bump from the old 250 pending an explicit backend max_page_size.
const exportPageSize = 500;

// Row cap for a single "download all". Bounds both the number of sequential page
// requests (maxExportRows / exportPageSize) and the rows held in memory before
// the CSV is built. Held at 5000 as a stopgap — large exports timed out around
// 6000 rows; lift it (or move to a server-prepared CSV) once the backend gains a
// bulk/streaming export endpoint.
export const maxExportRows = 5000;

function getResponse(status, message) {
  var responsePatch = {};
  responsePatch.status = status;
  responsePatch.message = message;
  return responsePatch;
}

function timeout(ms, promise) {
  return new Promise(function (resolve, reject) {
    setTimeout(function () {
      reject(new Error(ERROR_TIMEOUT));
    }, ms);
    promise.then(resolve, reject);
  });
}

function fetchApi(route, config) {
  return Promise.resolve(
    timeout(20000, fetch(route, config))
      .then((response) => {
        if (response.status === 401) {
          const auth = getAuth();
          signOut(auth);
        } else if (
          response.status === 404 &&
          response.url.includes("connect/admin/users")
        ) {
          return getResponse("error", "service_account");
        }
        return response
          .json()
          .then((data) => {
            data.status_code = response.status;
            // there is a json body
            return data;
          })
          .catch(() => {
            // there is no json body
            return getResponse("error", ERROR_UNKNOWN);
          });
      })
      .catch(() => {
        return getResponse("error", ERROR_TIMEOUT);
      }),
  );
}

export const callApi = (method, route, data, isFile) => {
  let config = {
    method: method,
    mode: "cors",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  };

  if (isFile) {
    config.headers = {};
  }

  if (data) {
    config.body = JSON.stringify(data);
    if (isFile) {
      config.body = data;
    }
  }

  return new Promise(function (resolve) {
    const auth = getAuth();
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (user) {
        // User is signed in.
        user
          .getIdToken(/* forceRefresh */ false)
          .then(function (idToken) {
            if (isTokenExpired(idToken)) {
              // Last login too long ago
              signOut(auth);
              unsubscribe();
              resolve(getResponse("error", ERROR_SESSION_EXPIRED));
            } else {
              config.headers.Authorization = `Bearer ${idToken}`;
              unsubscribe();
              resolve(fetchApi(route, config));
            }
          })
          .catch(function () {
            // Error retrieving token from firebase
            unsubscribe();
            resolve(getResponse("error", ERROR_TIMEOUT));
          });
      } else {
        unsubscribe();
        resolve(fetchApi(route, config));
      }
    });
  });
};

// The rows of one page. The totals endpoints answer with `totals` and no
// `next` when they don't paginate, and with `results` when they do.
//
// `results` is read first because this function is inside a loop that follows
// `next`: a response carrying both keys would be one page beside the whole
// day, and taking the whole day would append it again for every page. The
// unpaginated shape has no `results`, so it falls through, and the loop stops
// after one request anyway.
//
// Whichever key is read has to be an array — `totals` may be an aggregate
// object — and a key that isn't one must not shadow a key that is, which is
// why this picks the first array rather than the first key present.
//
// `undefined` when neither key is an array, which is a response this cannot
// read rather than a day with nothing in it: an empty day still answers with
// `results: []`, and that is an array. The caller turns the first into an
// error, because defaulting to `[]` here made the bookings card report "no
// bookings for today" for a response shape nobody had checked.
const pageRows = (res) =>
  [res.data?.results, res.data?.totals].find(Array.isArray);

// Fetch every page of a paginated ("results"/"next") endpoint by following the
// server's `next` URL, accumulating rows until the endpoint is exhausted or
// `maxRows` is reached. Used to export a whole view rather than the loaded page,
// and to read a totals endpoint whole rather than a page of it.
// Returns { status: "success", results, truncated }, or passes the error
// response through unchanged if any page request fails.
export const getAllResults = async (
  baseRoute,
  filters = {},
  { maxRows = maxExportRows, pageSize = exportPageSize, onProgress } = {},
) => {
  // page_size is the backend's query-param name; assign via a bracket key so the
  // API contract's snake_case doesn't trip camelCase linting.
  const query = { ...filters };
  query["page_size"] = pageSize;
  let res = await callApi("GET", filterObjToGetUrl(baseRoute, query));
  if (res.status !== "success") {
    return res;
  }

  const firstPage = pageRows(res);
  if (!firstPage) {
    // Neither key held an array, so there is nothing to read and no reason to
    // think the day is empty. Reported rather than defaulted: every caller
    // already handles a non-success status — the exporters raise a snackbar
    // and the bookings card shows its error panel — where a `[]` here reads
    // as a real answer.
    return {
      status: "error",
      message: "The server sent a response in a shape this page can't read.",
    };
  }

  // Shallow-copy so we never mutate (push into) the response's own array.
  const results = [...firstPage];
  let next = res.data?.next;
  onProgress?.(results.length);
  while (next && results.length < maxRows) {
    res = await callApi("GET", next);
    if (res.status !== "success") {
      return res;
    }
    const rows = pageRows(res);
    if (!rows) {
      return {
        status: "error",
        message: "The server sent a response in a shape this page can't read.",
      };
    }
    results.push(...rows);
    onProgress?.(results.length);
    next = res.data?.next;
  }

  const truncated = Boolean(next) || results.length > maxRows;
  if (results.length > maxRows) {
    results.length = maxRows;
  }
  return { status: "success", results, truncated };
};
